import { parse, format, isToday, isThisWeek, isAfter, startOfWeek, endOfWeek, addWeeks, isWithinInterval, isBefore, startOfDay, formatDistanceToNow } from "date-fns"

/**
 * Parse a date string in YYYY-MM-DD format to a Date object
 * This avoids timezone issues by treating the date as local
 */
export function parseDateString(dateString: string): Date {
  return parse(dateString, "yyyy-MM-dd", new Date())
}

/**
 * Format a Date object to YYYY-MM-DD string
 * This ensures consistent date format for API calls
 */
export function formatDateString(date: Date): string {
  return format(date, "yyyy-MM-dd")
}

/**
 * Format a date string for display (e.g., "January 15, 2024")
 */
export function formatDateForDisplay(dateString: string): string {
  return format(parseDateString(dateString), "MMM d, yyyy")
}

/**
 * Check if a date string represents today
 */
export function isDateStringToday(dateString: string): boolean {
  return isToday(parseDateString(dateString))
}

/**
 * Check if a date string is in the past (before today)
 */
export function isDateStringPast(dateString: string): boolean {
  const dueDate = startOfDay(parseDateString(dateString))
  const today = startOfDay(new Date())
  return isBefore(dueDate, today)
}

/**
 * Check if a date string is within this week
 */
export function isDateStringThisWeek(dateString: string): boolean {
  return isThisWeek(parseDateString(dateString))
}

/**
 * Check if a date string is within next week
 */
export function isDateStringNextWeek(dateString: string): boolean {
  const today = new Date()
  const nextWeekStart = startOfWeek(addWeeks(today, 1))
  const nextWeekEnd = endOfWeek(addWeeks(today, 1))
  const dueDate = parseDateString(dateString)
  
  return isWithinInterval(dueDate, { start: nextWeekStart, end: nextWeekEnd })
}

/**
 * Check if a date string is after next week
 */
export function isDateStringAfterNextWeek(dateString: string): boolean {
  const today = new Date()
  const nextWeekEnd = endOfWeek(addWeeks(today, 1))
  const dueDate = parseDateString(dateString)
  
  return isAfter(dueDate, nextWeekEnd)
}

/**
 * How long ago, for a timestamp on a record (INCO-179).
 *
 * A comment posted a second ago read **"in less than a minute"** — future
 * tense — for about ten seconds, then corrected itself to "just now" on the
 * next tick. The cause is clock skew: the server writes `created_at` from its
 * own clock, and if it is a few seconds ahead of the browser then the moment
 * the comment appears it is technically in the future, and
 * `formatDistanceToNow(…, { addSuffix: true })` duly says so.
 *
 * The old guard string-matched `"less than a minute ago"`, which is the *past*
 * tense form. The future one slipped straight through — which is why a
 * string comparison was the wrong tool: it can only catch the phrasings
 * somebody thought of.
 *
 * So: measure, do not match. Anything inside a minute either way is "Just
 * now", which is both true and what somebody who just pressed a button expects
 * to read.
 */
export function timeAgo(value: string | Date): string {
  const then = new Date(value)
  if (Number.isNaN(then.getTime())) return ""

  // Absolute, so a timestamp a few seconds in the future reads the same as one
  // a few seconds in the past. Both mean "just now" to a person. Lowercase,
  // like "3 minutes ago", so it reads right mid-sentence ("Sent just now").
  const elapsed = Math.abs(Date.now() - then.getTime())
  if (elapsed < 60_000) return "just now"

  // Clamped to now, so skew never produces "in 3 seconds" at the boundary.
  const safe = then.getTime() > Date.now() ? new Date() : then
  return formatDistanceToNow(safe, { addSuffix: true })
}

/**
 * "5m", "3h", "2d", "1mo": how long ago, for a list row with one line to spend
 * on it. The notifications popover and the inbox both use it, so the two lists
 * that say when something arrived say it the same way.
 */
export function shortAgo(at: string): string {
  const minutes = Math.max(1, Math.round((Date.now() - new Date(at).getTime()) / 60_000))
  if (minutes < 60) return `${minutes}m`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.round(hours / 24)
  if (days < 30) return `${days}d`
  return `${Math.round(days / 30)}mo`
}
